import "./event-agenda-item.scss";

import {
  Byline,
  BylineProps,
} from "design-system/components/primitives/byline/byline";
import { Link } from "design-system/components/primitives/link/link";
import { ReactNode } from "react";

interface LocationProps {
  label: string;
  link?: string;
}

export interface EventAgendaItemProps {
  times: Array<string>;
  title: string;
  subtitle?: ReactNode;
  location?: LocationProps;
  speakers?: BylineProps["authors"];
}

export function EventAgendaItem({
  times,
  title,
  subtitle,
  location,
  speakers,
}: EventAgendaItemProps) {
  const startTime = times[0];
  const endTime = times[1];
  const hasEndTime = endTime && times.length > 1;

  return (
    <li className="hbs-event-agenda-item">
      <div className="hbs-event-agenda-item__time">
        <div>
          {startTime}
          {hasEndTime ? (
            <>
              <span className="hbs-global-visually-hidden"> to </span>
              <span aria-hidden="true"> — </span>
            </>
          ) : (
            ""
          )}
        </div>
        {endTime && <div>{endTime}</div>}
      </div>
      <div className="hbs-event-agenda-item__content">
        <h3 className="hbs-event-agenda-item__title">{title}</h3>
        {subtitle && (
          <p className="hbs-event-agenda-item__subtitle">{subtitle}</p>
        )}
        <div className="hbs-event-agenda-item__location">
          {location && (
            <div className="hbs-event-agenda-item__location-item">
              <h4 className="hbs-event-agenda-item__info-title">Location</h4>
              <p className="hbs-event-agenda-item__info-location">
                {location.link ? (
                  <Link href={location.link}>{location?.label}</Link>
                ) : (
                  <>{location.label}</>
                )}
              </p>
            </div>
          )}
        </div>
        <div className="hbs-event-agenda-item__info">
          {speakers?.length
            ? (() => {
                const typedGroups = new Map<string, typeof speakers>();
                const untyped: typeof speakers = [];

                speakers.forEach((speaker) => {
                  const types = speaker.contributorType?.filter(Boolean) ?? [];
                  if (types.length === 0) {
                    untyped.push(speaker);
                    return;
                  }
                  types.forEach((t) => {
                    typedGroups.set(t, [
                      ...(typedGroups.get(t) ?? []),
                      speaker,
                    ]);
                  });
                });

                if (untyped.length > 0) {
                  const existingSpeakerGroup = typedGroups.get("Speaker") ?? [];
                  typedGroups.set("Speaker", [
                    ...existingSpeakerGroup,
                    ...untyped,
                  ]);
                }

                const groups: Array<{ type: string; people: typeof speakers }> =
                  [
                    ...Array.from(typedGroups.entries()).map(
                      ([type, people]) => ({
                        type,
                        people,
                      }),
                    ),
                  ];

                return groups.map(({ type, people }) => (
                  <div className="hbs-event-agenda-item__info-item" key={type}>
                    <h4 className="hbs-event-agenda-item__info-title">
                      {type}
                      {people.length > 1 && "s"}
                    </h4>
                    <div className="hbs-event-agenda-item__info-speakers">
                      <Byline
                        featuredAuthors={people}
                        type={"PeopleOnly"}
                        headshots={false}
                      />
                    </div>
                  </div>
                ));
              })()
            : null}
        </div>
      </div>
    </li>
  );
}
